import { useState, useEffect, useMemo } from 'react'
import commissioningAuthoritiesService from '../services/commissioningAuthoritiesService'

const chipClass = 'inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium bg-brand-navy/5 text-brand-navy border border-brand-navy/10'
const UNTYPED = 'Unassigned'

/**
 * Commissioning Authorities — Enterprise Standard
 * Lets admins maintain the commissioning authority taxonomy: current
 * Authority Name, Authority Type, Nation and Legacy Names. Saved values are
 * cached server-side and picked up by tender matching, ingestion and the
 * organisation profile picker after a save (no deploy needed). Authorities
 * are deactivated rather than deleted once organisations reference them.
 */
export default function CommissioningAuthorities() {
    const [rows, setRows] = useState([])
    const [options, setOptions] = useState({ authorityTypes: [], nations: [] })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState('')
    const [nationFilter, setNationFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [expanded, setExpanded] = useState(() => new Set())
    const [showAddForm, setShowAddForm] = useState(false)

    useEffect(() => { load() }, [])

    const load = async () => {
        setLoading(true)
        setError(null)
        try {
            const [data, opts] = await Promise.all([
                commissioningAuthoritiesService.getAll(),
                commissioningAuthoritiesService.getOptions(),
            ])
            setRows(data)
            setOptions(opts)
        } catch (err) {
            setError(err.message || 'Failed to load commissioning authorities')
        } finally {
            setLoading(false)
        }
    }

    const typeLabel = useMemo(() => {
        const map = new Map(options.authorityTypes.map(o => [o.value, o.label]))
        return (slug) => map.get(slug) || slug
    }, [options])

    const nationLabel = useMemo(() => {
        const map = new Map(options.nations.map(o => [o.value, o.label]))
        return (value) => map.get(value) || value
    }, [options])

    const grouped = useMemo(() => {
        const term = search.trim().toLowerCase()
        const filtered = rows.filter(r => {
            if (typeFilter && !r.authorityTypes.includes(typeFilter)) return false
            if (nationFilter && !r.nations.includes(nationFilter)) return false
            if (statusFilter === 'active' && !r.isActive) return false
            if (statusFilter === 'inactive' && r.isActive) return false
            if (!term) return true
            return r.authorityName.toLowerCase().includes(term) ||
                r.legacyNames.some(n => n.toLowerCase().includes(term))
        })

        const map = new Map()
        for (const row of filtered) {
            const key = row.authorityTypes[0] || UNTYPED
            if (!map.has(key)) map.set(key, [])
            map.get(key).push(row)
        }
        for (const list of map.values()) list.sort((a, b) => a.authorityName.localeCompare(b.authorityName))
        return Array.from(map.entries())
            .map(([key, list]) => ({ key, label: key === UNTYPED ? UNTYPED : typeLabel(key), rows: list }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }, [rows, search, typeFilter, nationFilter, statusFilter, typeLabel])

    const toggleGroup = (key) => {
        setExpanded(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const handleRowSaved = (updated) => setRows(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)))
    const handleDeleted = (id) => setRows(prev => prev.filter(r => r.id !== id))
    const handleCreated = (created) => {
        setRows(prev => [...prev, created])
        setShowAddForm(false)
        setExpanded(prev => new Set(prev).add(created.authorityTypes[0] || UNTYPED))
    }

    const isFiltering = Boolean(search.trim() || typeFilter || nationFilter || statusFilter)
    const total = grouped.reduce((n, g) => n + g.rows.length, 0)

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Commissioning Authorities</h1>
                    <p className="text-sm text-admin-muted mt-1 max-w-2xl">
                        Add, edit or deactivate commissioning authorities, set their type and nation, and record
                        previous names (Legacy Names) so older tenders and saved organisation selections keep
                        matching. Saved changes are applied to tender matching shortly after saving.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setShowAddForm(v => !v)} className="btn-primary text-xs whitespace-nowrap">
                        {showAddForm ? 'Cancel' : 'New Authority'}
                    </button>
                    <button onClick={load} disabled={loading} className="btn-secondary text-xs whitespace-nowrap">
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {showAddForm && (
                <AuthorityForm
                    title="New Authority"
                    submitLabel="Create Authority"
                    options={options}
                    onSubmit={async (values) => handleCreated(await commissioningAuthoritiesService.create(values))}
                    onCancel={() => setShowAddForm(false)}
                />
            )}

            <div className="flex flex-wrap gap-3">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search authority name or legacy name..."
                    className="input-admin w-full max-w-md"
                />
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="input-admin text-sm">
                    <option value="">All Authority Types</option>
                    {options.authorityTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={nationFilter} onChange={(e) => setNationFilter(e.target.value)} className="input-admin text-sm">
                    <option value="">All Nations</option>
                    {options.nations.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-admin text-sm">
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>

            {error && (
                <div className="p-4 bg-status-error-bg border border-status-error/20 rounded-lg text-sm text-status-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="card-admin p-8 text-center text-sm text-admin-muted">Loading commissioning authorities...</div>
            ) : grouped.length === 0 ? (
                <div className="card-admin p-8 text-center text-sm text-admin-muted">No matching commissioning authorities.</div>
            ) : (
                <div className="space-y-3">
                    {grouped.map((group) => {
                        const open = isFiltering || expanded.has(group.key)
                        return (
                            <div key={group.key} className="card-admin overflow-hidden">
                                <button
                                    onClick={() => toggleGroup(group.key)}
                                    className="w-full px-6 py-4 bg-gray-50 flex items-center justify-between gap-4 text-left hover:bg-gray-100 transition-colors"
                                >
                                    <div>
                                        <h2 className="text-sm font-bold text-admin-heading">{group.label}</h2>
                                        <p className="text-xs text-admin-muted mt-0.5">
                                            {group.rows.length} authorit{group.rows.length === 1 ? 'y' : 'ies'}
                                        </p>
                                    </div>
                                    <svg
                                        className={`w-4 h-4 text-admin-muted transition-transform ${open ? 'rotate-180' : ''}`}
                                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {open && (
                                    <div className="divide-y divide-admin-border">
                                        {group.rows.map((row) => (
                                            <AuthorityRow
                                                key={row.id}
                                                row={row}
                                                options={options}
                                                typeLabel={typeLabel}
                                                nationLabel={nationLabel}
                                                onSaved={handleRowSaved}
                                                onDeleted={handleDeleted}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                    <p className="text-xs text-admin-muted">{total} authorit{total === 1 ? 'y' : 'ies'} shown</p>
                </div>
            )}
        </div>
    )
}

/** Toggle-chip multi-select for Authority Type / Nation. */
function MultiSelect({ label, hint, options, value, onChange }) {
    const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v])
    return (
        <div>
            <label className="block text-xs font-medium text-admin-muted mb-1">{label}</label>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => {
                    const on = value.includes(o.value)
                    return (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => toggle(o.value)}
                            aria-pressed={on}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on
                                ? 'bg-brand-navy text-white border-brand-navy'
                                : 'bg-white text-brand-navy border-brand-navy/20 hover:bg-brand-navy/5'}`}
                        >
                            {o.label}
                        </button>
                    )
                })}
            </div>
            {hint && <p className="text-xs text-admin-muted mt-1">{hint}</p>}
        </div>
    )
}

/** Tag/chip list editor for Legacy Names. */
function LegacyNamesEditor({ names, onChange }) {
    const [draft, setDraft] = useState('')

    const add = () => {
        const v = draft.trim()
        if (!v) return
        if (!names.some(n => n.toLowerCase() === v.toLowerCase())) onChange([...names, v])
        setDraft('')
    }

    return (
        <div>
            <label className="block text-xs font-medium text-admin-muted mb-1">Legacy Names (previous or alternative authority names)</label>
            <div className="flex flex-wrap gap-2 mb-2">
                {names.map((name) => (
                    <span key={name} className={chipClass}>
                        {name}
                        <button
                            type="button"
                            onClick={() => onChange(names.filter(n => n !== name))}
                            className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-brand-navy/10 text-brand-navy/60 hover:text-brand-navy"
                            aria-label={`Remove ${name}`}
                        >
                            ×
                        </button>
                    </span>
                ))}
                {names.length === 0 && <span className="text-xs text-admin-muted italic">No legacy names.</span>}
            </div>
            <div className="flex gap-2 max-w-md">
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
                    placeholder="Add a legacy name..."
                    className="input-admin flex-1 text-sm py-1.5"
                />
                <button type="button" onClick={add} disabled={!draft.trim()} className="btn-secondary text-xs px-3 disabled:opacity-40">
                    Add
                </button>
            </div>
        </div>
    )
}

/** Shared create/edit form. `initial` omitted = create. */
function AuthorityForm({ title, submitLabel, options, initial, renameNote, onSubmit, onCancel }) {
    const [authorityName, setAuthorityName] = useState(initial?.authorityName || '')
    const [authorityTypes, setAuthorityTypes] = useState(initial?.authorityTypes || [])
    const [nations, setNations] = useState(initial?.nations || [])
    const [legacyNames, setLegacyNames] = useState(initial?.legacyNames || [])
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)

    const renaming = Boolean(initial) && authorityName.trim() && authorityName.trim() !== initial.authorityName

    const submit = async () => {
        setError(null)
        if (!authorityName.trim()) { setError('Authority Name is required.'); return }
        if (authorityTypes.length === 0) { setError('Select at least one Authority Type.'); return }
        setSaving(true)
        try {
            await onSubmit({ authorityName: authorityName.trim(), authorityTypes, nations, legacyNames })
        } catch (err) {
            setError(err.message || 'Failed to save commissioning authority')
            setSaving(false)
        }
    }

    return (
        <div className="card-admin p-6 space-y-4">
            {title && <h2 className="text-sm font-bold text-admin-heading">{title}</h2>}

            {error && (
                <div className="p-3 bg-status-error-bg border border-status-error/20 rounded-lg text-sm text-status-error">
                    {error}
                </div>
            )}

            <div className="max-w-xl">
                <label className="block text-xs font-medium text-admin-muted mb-1">Authority Name (current)</label>
                <input
                    type="text"
                    value={authorityName}
                    onChange={(e) => setAuthorityName(e.target.value)}
                    placeholder="e.g. Barnet Council"
                    className="input-admin w-full text-sm"
                />
                {renameNote && (
                    <p className={`text-xs mt-1 ${renaming ? 'text-status-warning' : 'text-admin-muted'}`}>
                        Renaming keeps the previous name as a Legacy Name and updates the saved selections of
                        organisations that chose this authority.
                    </p>
                )}
            </div>

            <MultiSelect
                label="Authority Type"
                options={options.authorityTypes}
                value={authorityTypes}
                onChange={setAuthorityTypes}
            />
            <MultiSelect
                label="Nation"
                hint="Leave empty for UK-wide bodies not tied to one nation."
                options={options.nations}
                value={nations}
                onChange={setNations}
            />
            <LegacyNamesEditor names={legacyNames} onChange={setLegacyNames} />

            <div className="flex items-center gap-2">
                <button onClick={submit} disabled={saving} className="btn-primary text-xs px-4 py-2 disabled:opacity-40">
                    {saving ? 'Saving...' : submitLabel}
                </button>
                <button onClick={onCancel} disabled={saving} className="btn-secondary text-xs px-4 py-2">
                    Cancel
                </button>
            </div>
        </div>
    )
}

function AuthorityRow({ row, options, typeLabel, nationLabel, onSaved, onDeleted }) {
    const [editing, setEditing] = useState(false)
    const [busy, setBusy] = useState(false)
    const [status, setStatus] = useState(null)

    const flash = (msg) => {
        setStatus(msg)
        if (msg === 'Saved') setTimeout(() => setStatus(null), 2500)
    }

    const handleSave = async (values) => {
        const updated = await commissioningAuthoritiesService.update(row.id, values)
        onSaved(updated)
        setEditing(false)
        flash('Saved')
    }

    const toggleActive = async () => {
        const next = !row.isActive
        if (!next && !window.confirm(`Deactivate "${row.authorityName}"? It will no longer be offered to organisations, but existing selections and matching are kept.`)) return
        setBusy(true)
        setStatus(null)
        try {
            onSaved(await commissioningAuthoritiesService.update(row.id, { isActive: next }))
            flash('Saved')
        } catch (err) {
            flash(err.message || 'Failed to update status')
        } finally {
            setBusy(false)
        }
    }

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${row.authorityName}"? This cannot be undone.`)) return
        setBusy(true)
        setStatus(null)
        try {
            await commissioningAuthoritiesService.remove(row.id)
            onDeleted(row.id)
        } catch (err) {
            // 409 when organisations still reference it
            flash(`${err.message || 'Failed to delete'} — deactivate it instead if it is still in use.`)
            setBusy(false)
        }
    }

    if (editing) {
        return (
            <div className="px-6 py-4">
                <AuthorityForm
                    submitLabel="Save"
                    options={options}
                    initial={row}
                    renameNote
                    onSubmit={handleSave}
                    onCancel={() => setEditing(false)}
                />
            </div>
        )
    }

    return (
        <div className={`px-6 py-4 ${row.isActive ? '' : 'bg-gray-50/60'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-semibold ${row.isActive ? 'text-admin-heading' : 'text-admin-muted'}`}>{row.authorityName}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${row.isActive
                            ? 'bg-status-success-bg text-status-success border-status-success/20'
                            : 'bg-gray-100 text-admin-muted border-admin-border'}`}>
                            {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <p className="text-xs text-admin-muted mt-1">
                        <span className="font-medium">Authority Type:</span>{' '}
                        {row.authorityTypes.length ? row.authorityTypes.map(typeLabel).join(', ') : '—'}
                        <span className="mx-2">·</span>
                        <span className="font-medium">Nation:</span>{' '}
                        {row.nations.length ? row.nations.map(nationLabel).join(', ') : 'UK-wide / not specified'}
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    {status === 'Saved' && <span className="text-xs text-status-success font-medium">Saved</span>}
                    {status && status !== 'Saved' && <span className="text-xs text-status-error font-medium max-w-xs text-right">{status}</span>}
                    <button onClick={() => setEditing(true)} disabled={busy} className="btn-secondary text-xs px-3 py-1.5">
                        Edit
                    </button>
                    <button onClick={toggleActive} disabled={busy} className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">
                        {row.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-lg border border-status-error/30 text-status-error hover:bg-status-error-bg disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Delete
                    </button>
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-admin-muted">Legacy Names:</span>
                {row.legacyNames.map((name) => (
                    <span key={name} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-navy/5 text-brand-navy border border-brand-navy/10">
                        {name}
                    </span>
                ))}
                {row.legacyNames.length === 0 && <span className="text-xs text-admin-muted italic">None</span>}
            </div>
        </div>
    )
}
